import { Component, computed, signal, effect, OnInit } from '@angular/core';
import { TaxonomyService } from '../../../services/taxonomy.service';
import { TaxonomyApiService } from '../../../services/api-services/taxonomy-api.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { Router } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-taxonomy-list',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    CommonModule,
    FormsModule,
    MatMenuModule,
    MatButtonModule,
    MatInputModule,
  ],
  templateUrl: './taxonomy-list.component.html',
  styleUrl: './taxonomy-list.component.scss',
})
export class TaxonomyListComponent implements OnInit {
  searchText = signal<string>('');
  options = computed(() => this.taxonomyService._list_of_taxonomies());
  private lastSelectedTaxonomyId: string | null = null;

  filteredOptions = computed(() => {
    const search = this.searchText().toLowerCase();
    return this.options().filter((option) => option.taxonomy_name.toLowerCase().includes(search));
  });

  _selectedValue = this.taxonomyService.getSelectedValue();
  taxoSelectedValue = computed(() => this._selectedValue()?.taxonomy_name || 'Select Taxonomy');

  constructor(
    public taxonomyService: TaxonomyService,
    private router: Router,
    private taxonomyApiService: TaxonomyApiService
  ) {
    // Initialize lastSelectedTaxonomyId from localStorage
    const storedData = sessionStorage.getItem('selectedTaxonomy');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        this.lastSelectedTaxonomyId = parsedData.taxonomy_id;
      } catch (error) {
        console.error('Error parsing localStorage data:', error);
      }
    }

    // Set up effect to watch for changes
    effect(() => {
      const currentValue = this._selectedValue();
      if (currentValue) {
        const currentTaxonomyId = currentValue.taxonomy_id;
        if (this.lastSelectedTaxonomyId !== currentTaxonomyId) {
          this.lastSelectedTaxonomyId = currentTaxonomyId;
          // Reload the browser when taxonomy changes
          window.location.reload();
        }
      }
    });
  }

  ngOnInit() {
    // Subscribe to updates to refresh the taxonomy list
    this.taxonomyService.updateTaxonomy$.subscribe(() => {
      this.taxonomyService.loadTaxonomy();
    });
  }

  onSelectionChange(value: string) {
    const completeTaxonomy = this.taxonomyService._taxonomiesData().find((taxonomy) => taxonomy.taxonomy_id === value);

    if (completeTaxonomy) {
      // Fetch complete version history data to get all required fields
      this.taxonomyApiService.getVersionHistoryById({ taxonomy_id: value }).subscribe({
        next: (response) => {
          const version_history_details = response.data.version_history_details || [];
          const selected_taxonomy_version = version_history_details[0] || null;

          this.taxonomyService.setSelectedValue({
            taxonomy_id: completeTaxonomy.taxonomy_id,
            taxonomy_name: completeTaxonomy.taxonomy_name,
            version_history_details: version_history_details,
            selected_taxonomy_version: selected_taxonomy_version,
          });
        },
        error: (err) => {
          console.error('Failed to fetch version history for taxonomy selection:', err);
          // Fallback to using the basic taxonomy data
          this.taxonomyService.setSelectedValue({
            taxonomy_id: completeTaxonomy.taxonomy_id,
            taxonomy_name: completeTaxonomy.taxonomy_name,
            version_history_details: completeTaxonomy.version_history_details || [],
            selected_taxonomy_version: completeTaxonomy.version_history_details?.[0] || null,
          });
        },
      });
    } else {
      this.taxonomyService.setSelectedValue(null);
    }
  }
}
